import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import RequiredMark from '@/shein-platform/components/RequiredMark';
import SheinSwitch from '@/shein-platform/components/SheinSwitch';
import {
  buildSizeChartTemplatePayload,
  validateSizeChartForm,
} from '@/shein-platform/utils/sheinSizeChart';
import { nextTemplatePriority } from '@/shein-platform/utils/sheinTemplatePriority';
import { sheinPlatformAPI } from '@/utils/api';
import { adminAPI } from '@/utils/adminApi';
import { showToast } from '@/utils/toast';
import { chartText } from '@/i18n/chartText';
import { useTranslation } from 'react-i18next';

/**
 * 另存尺码库：元信息弹窗（字段/样式对齐属性库保存弹窗）。
 */
export default function SaveSizeChartTemplateMetaModal({
  open,
  shopId,
  officialMode = false,
  shopType = '',
  categoryId = '',
  productTypeId = '',
  categoryPathLabel = '',
  sizeSaleAttributeId = '',
  sizeChart = {},
  chartRows = [],
  measureAttrs = [],
  sizeAttr = null,
  selectedSizeIds = [],
  skcList = [],
  onClose,
  onSaved,
}) {
  useTranslation('workbench');
  const [name, setName] = useState('');
  const [styleNo, setStyleNo] = useState('');
  const [remark, setRemark] = useState('');
  const [autoRuleEnabled, setAutoRuleEnabled] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);
  const [saving, setSaving] = useState(false);
  const [priorityLoading, setPriorityLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setStyleNo('');
    setRemark('');
    setAutoRuleEnabled(false);
    setSortOrder(0);
    setSaving(false);
  }, [open]);

  useEffect(() => {
    if (!open || !productTypeId || (!officialMode && !shopId)) return undefined;
    let cancelled = false;
    setPriorityLoading(true);
    const load = officialMode
      ? adminAPI.listOfficialSheinSizeChartTemplates({
        productTypeId,
        shopType,
        page: 1,
        pageSize: 100,
      })
      : sheinPlatformAPI.listSizeChartTemplates(shopId, {
        productTypeId,
        page: 1,
        pageSize: 100,
      });
    load
      .then((res) => {
        if (cancelled) return;
        setSortOrder(nextTemplatePriority(res?.items));
      })
      .catch(() => {
        if (!cancelled) setSortOrder(0);
      })
      .finally(() => {
        if (!cancelled) setPriorityLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, productTypeId, officialMode, shopType]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const submit = async () => {
    if (!officialMode && !shopId) {
      showToast(chartText('returnList.pickShop', fixedUi("请先选择店铺")), 'error');
      return;
    }
    const styleNoValue = String(styleNo || '').trim();
    if (!styleNoValue) {
      showToast(chartText('saveTpl.needStyle', fixedUi("请填写款号")), 'error');
      return;
    }
    const title = String(name || '').trim();
    if (!title) {
      showToast(chartText('saveTpl.needName', fixedUi("请填写模板名称")), 'error');
      return;
    }
    if (!categoryId || !productTypeId) {
      showToast(chartText('saveTpl.needCategory', fixedUi("缺少适用类目")), 'error');
      return;
    }
    const payload = buildSizeChartTemplatePayload({
      sizeChart,
      chartRows,
      measureAttrs,
      sizeSaleAttributeId,
      productTypeId,
      categoryId,
      categoryPathLabel,
    });
    if (!payload.sizeChart || !Object.keys(payload.sizeChart).length) {
      showToast(chartText('saveTpl.emptyChart', '当前尺码表为空，无法保存'), 'error');
      return;
    }
    const sizeCheck = validateSizeChartForm({
      sizeChart,
      measureAttrs,
      sizeAttr,
      selectedSizeIds,
      skcList,
    });
    if (!sizeCheck.ok) {
      showToast(sizeCheck.message || chartText('saveTpl.chartInvalid', '尺码表不符合平台填写要求'), 'error');
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: title,
        styleNo: styleNoValue,
        remark: String(remark || '').trim() || undefined,
        productTypeId,
        categoryId,
        categoryPathLabel: categoryPathLabel || undefined,
        sizeSaleAttributeId: sizeSaleAttributeId || undefined,
        autoRuleEnabled,
        sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
        payload,
      };
      if (officialMode) {
        body.shopType = shopType;
      }
      if (officialMode) {
        await adminAPI.createOfficialSheinSizeChartTemplate(body);
      } else {
        await sheinPlatformAPI.createSizeChartTemplate(shopId, body);
      }
      showToast(officialMode ? chartText('saveTpl.savedOfficialSize', '已保存到官方尺码库') : chartText('saveTpl.savedSize', '已保存到尺码库'), 'success');
      onSaved?.();
      onClose?.();
    } catch (e) {
      showToast(e?.message || chartText('saveTpl.saveSizeFail', '保存到尺码库失败'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="spo-batch-modal spo-attr-tpl-modal" role="presentation">
      <button
        type="button"
        className="spo-batch-modal__backdrop"
        aria-label={chartText('moduleShell.close', fixedUi("关闭"))}
        onClick={() => { if (!saving) onClose?.(); }}
      />
      <div
        className="spo-batch-modal__panel spo-attr-tpl-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="spo-size-chart-save-meta-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="spo-batch-modal__head">
          <h2 id="spo-size-chart-save-meta-title">{officialMode ? chartText('saveTpl.saveOfficialSize', fixedUi("保存到官方尺码库")) : chartText('saveTpl.saveSize', fixedUi("保存到尺码库"))}</h2>
          <button type="button" className="spo-batch-modal__close" aria-label={chartText('moduleShell.close', fixedUi("关闭"))} disabled={saving} onClick={onClose}>×</button>
        </div>
        <div className="spo-batch-modal__body">
          <div className="spo-wizard__form spo-template-create-form">
            <label className="spo-field spo-field--full">
              <span className="spo-field__label">{chartText('productTemplate.colStyleNo', fixedUi("款号"))} <RequiredMark /></span>
              <input
                className="spo-input"
                value={styleNo}
                disabled={saving}
                maxLength={128}
                placeholder={chartText('saveTpl.stylePh', fixedUi("请输入款号"))}
                onChange={(e) => setStyleNo(e.target.value)}
              />
            </label>
            <label className="spo-field spo-field--full">
              <span className="spo-field__label">{chartText('productTemplate.colName', fixedUi("模板名称"))} <RequiredMark /></span>
              <input
                className="spo-input"
                value={name}
                disabled={saving}
                maxLength={128}
                placeholder={chartText('saveTpl.namePhSize', '如：女装基础尺码表')}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="spo-field spo-field--full">
              <span className="spo-field__label">{chartText('productTemplate.colRemark', fixedUi("备注"))}</span>
              <input
                className="spo-input"
                value={remark}
                disabled={saving}
                maxLength={200}
                placeholder={chartText('publishSpec.patternOptional', fixedUi("可选"))}
                onChange={(e) => setRemark(e.target.value)}
              />
            </label>
            <div className="spo-field spo-field--full spo-template-auto-rule">
              <div className="spo-template-auto-rule__switch">
                <SheinSwitch
                  checked={autoRuleEnabled}
                  disabled={saving}
                  ariaLabel={chartText('productTemplate.colAutoRule', '自动规则')}
                  onChange={setAutoRuleEnabled}
                />
                <span>{chartText('productTemplate.colAutoRule', fixedUi("自动规则"))}</span>
              </div>
              <p className="spo-wizard__inline-hint">
                {chartText('saveTpl.autoHint', '开启后，发品选中该类目时自动带出。优先级仅在同类目内比较，数字越小越优先。')}
              </p>
              <label className="spo-field">
                <span className="spo-field__label">{chartText('productTemplate.colPriority', fixedUi("优先级"))}</span>
                <input
                  className="spo-input"
                  type="number"
                  step={1}
                  value={sortOrder}
                  disabled={saving || priorityLoading}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setSortOrder(Number.isFinite(next) ? next : 0);
                  }}
                />
              </label>
            </div>
            <label className="spo-field spo-field--full">
              <span className="spo-field__label">{chartText('photoLib.categoryApply', fixedUi("适用类目"))}</span>
              <input
                className="spo-input"
                value={categoryPathLabel || categoryId || '—'}
                disabled
                readOnly
              />
            </label>
          </div>
        </div>
        <div className="spo-batch-modal__foot">
          <button type="button" className="spo-btn spo-btn--ghost spo-btn--sm" disabled={saving} onClick={onClose}>
            {chartText('shop.cancel', '取消')}
          </button>
          <button
            type="button"
            className="spo-btn spo-btn--primary spo-btn--sm"
            disabled={saving || (!officialMode && !shopId)}
            onClick={submit}
          >
            {saving ? chartText('tiktokPublish.saving', fixedUi("保存中…")) : chartText('shop.save', fixedUi("保存"))}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
